import React, { useState } from 'react';
import { Plus, Edit } from 'lucide-react';
import FormModal from './FormModal';
import type { AccessControlRule } from '@domains/admin/api/accessControl.adapter';

interface AccessRuleModalProps {
  onClose: () => void;
  onSubmit: (rule: AccessControlRule) => Promise<void> | void;
  selectedRule: AccessControlRule | null;
}

export const AccessRuleModal: React.FC<AccessRuleModalProps> = ({
  onClose,
  onSubmit,
  selectedRule,
}) => {
  const [rule, setRule] = useState<AccessControlRule>(
    selectedRule || {
      id: 0,
      name: '',
      description: '',
      ruleType: 'ip_whitelist',
      ruleValue: '',
      isActive: true,
      priority: 1,
      createdAt: '',
      updatedAt: '',
    }
  );

  const handleSubmit = async (data: any) => {
    try {
      await onSubmit({ ...rule, ...data });
      onClose();
    } catch {
      // error already handled by parent; keep modal open
    }
  };

  const isCreate = !selectedRule?.id;

  return (
    <FormModal
      title={isCreate ? 'Create Access Rule' : 'Edit Access Rule'}
      subtitle={isCreate ? 'Create a new access control rule' : 'Modify existing access rule'}
      icon={isCreate ? <Plus className="text-white" size={24} /> : <Edit className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Rule' : 'Update Rule'}
      cancelText="Cancel"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-slate-700">
            Rule Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={rule.name}
            onChange={(e) => setRule({ ...rule, name: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="e.g., Office IP Whitelist"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="description" className="block text-sm font-medium text-slate-700">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={rule.description}
            onChange={(e) => setRule({ ...rule, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="Describe what this rule does"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="ruleType" className="block text-sm font-medium text-slate-700">
            Rule Type *
          </label>
          <select
            id="ruleType"
            name="ruleType"
            value={rule.ruleType}
            onChange={(e) => setRule({ ...rule, ruleType: e.target.value as any })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
          >
            <option value="ip_whitelist">IP Whitelist</option>
            <option value="ip_blacklist">IP Blacklist</option>
            <option value="time_restriction">Time Restriction</option>
            <option value="geo_restriction">Geo Restriction</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div className="space-y-2">
          <label htmlFor="priority" className="block text-sm font-medium text-slate-700">
            Priority *
          </label>
          <input
            type="number"
            id="priority"
            name="priority"
            required
            min="1"
            max="100"
            value={rule.priority}
            onChange={(e) => setRule({ ...rule, priority: parseInt(e.target.value) })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="1-100"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="ruleValue" className="block text-sm font-medium text-slate-700">
            Rule Value *
          </label>
          <textarea
            id="ruleValue"
            name="ruleValue"
            required
            value={rule.ruleValue}
            onChange={(e) => setRule({ ...rule, ruleValue: e.target.value })}
            rows={4}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="Enter the rule value (IP addresses, time ranges, etc.)"
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={rule.isActive}
            onChange={(e) => setRule({ ...rule, isActive: e.target.checked })}
            className="h-5 w-5 text-slate-600 rounded focus:ring-2 focus:ring-slate-500 focus:ring-offset-0"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-slate-700">
            Rule is active
          </label>
        </div>
      </div>
    </FormModal>
  );
};